import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { useAppForm } from "@/lib/form/form-hook";
import { createChangePasswordSchema } from "@/lib/form/schemas/auth";
import { isStaleSessionError } from "@/lib/api/client";
import { useChangePassword } from "../hooks/useChangePassword";
import { StaleSessionNotice } from "./StaleSessionNotice";

export function ChangePassword() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const mutation = useChangePassword();
  const schema = createChangePasswordSchema(t);

  const form = useAppForm({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validators: {
      onBlur: schema,
    },
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value }) => {
      await mutation
        .mutateAsync({ currentPassword: value.currentPassword, newPassword: value.newPassword })
        .catch(() => undefined);
    },
  });

  const staleSession = isStaleSessionError(mutation.error);

  const close = () => {
    if (mutation.isPending) return;
    setOpen(false);
    form.reset();
    mutation.reset();
  };

  const openDialog = () => {
    form.reset();
    mutation.reset();
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="rounded-lg border border-border bg-surface px-3.5 py-2 text-xs font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
      >
        {t("account.changePassword")}
      </button>

      <Dialog
        open={open}
        onClose={close}
        title={t("account.password.title")}
        description={t("account.password.subtitle")}
      >
        {mutation.isSuccess ? (
          <div className="space-y-4">
            <Alert variant="success">{t("account.password.success")}</Alert>
            <button
              type="button"
              onClick={close}
              autoFocus
              className="w-full rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright"
            >
              {t("common.close")}
            </button>
          </div>
        ) : (
          <form.AppForm>
            <form.Form className="flex flex-col gap-4" noValidate>
              <form.AppField
                name="currentPassword"
                children={(field) => (
                  <field.PasswordField
                    label={t("account.password.current")}
                    autoComplete="current-password"
                    readOnly={mutation.isPending}
                  />
                )}
              />
              <form.AppField
                name="newPassword"
                children={(field) => (
                  <field.PasswordField
                    label={t("account.password.new")}
                    autoComplete="new-password"
                    readOnly={mutation.isPending}
                  />
                )}
              />
              <form.AppField
                name="confirmPassword"
                children={(field) => (
                  <field.PasswordField
                    label={t("account.password.confirm")}
                    autoComplete="new-password"
                    readOnly={mutation.isPending}
                  />
                )}
              />

              {mutation.error && !staleSession && (
                <ApiErrorBanner
                  error={mutation.error}
                  fieldLabels={{
                    currentPassword: t("account.password.current"),
                    newPassword: t("account.password.new"),
                  }}
                />
              )}
              {staleSession && (
                <StaleSessionNotice
                  returnTo="/account"
                  body={t("account.staleSession.passwordBody")}
                  autoFocus
                />
              )}

              <form.SubmitButton
                label={t("account.password.submit")}
                loadingLabel={t("account.password.submitting")}
                disabled={staleSession}
              />
              <button
                type="button"
                onClick={close}
                aria-disabled={mutation.isPending || undefined}
                className="text-sm font-medium text-muted transition-colors hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
              >
                {t("common.cancel")}
              </button>
            </form.Form>
          </form.AppForm>
        )}
      </Dialog>
    </>
  );
}
